import { useEffect } from 'react'
import { InferNative, Sources, Type } from '~/prix'
import { BaseActionDefinitionEnhanced } from '~/prix/actionDefinition'
import useActionFunction from './actionFunction'
import { SourcesEnhanced } from '~/prix/sources/source'

export default function useAction<
  InputType extends Type,
  OutputType extends Type,
  ActionDefinition extends BaseActionDefinitionEnhanced<InputType, OutputType>,
  Input = InferNative<InputType>,
  Output = InferNative<OutputType>,
>(
  actionDefinition: ActionDefinition,
  input: Input,
  deps: React.DependencyList = input && typeof input === 'object' ? Object.values(input) : [input],
  {
    autoLoad = true,
    staleWhileRevalidate = false,
    sourceKey = 'oltp',
  }: {
    autoLoad?: boolean
    staleWhileRevalidate?: boolean
    sourceKey?: keyof SourcesEnhanced
  } = {},
) {
  const { callAction, result, error, isLoading } = useActionFunction<
    InputType,
    OutputType,
    ActionDefinition,
    Input,
    Output
  >(actionDefinition, {
    sourceKey,
    staleWhileRevalidate,
    initialIsLoading: autoLoad,
  })

  useEffect(() => {
    if (!autoLoad) {
      return
    }
    const { abort, resultPromise } = callAction(input)
    resultPromise.catch(error => {
      console.error(error)
    })
    return () => {
      abort()
    }
  }, [callAction, autoLoad, ...deps])

  return {
    result,
    error,
    isLoading,
    callAction,
  }
}
